// src/app/services/badge.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface BadgeConfig {
  id: number;
  businessHubId: number;
  enabled: boolean;
  requiredAmount: number;
  bonusCoins: number;
  badgeName: string;
  badgeDescription?: string;
  badgeIcon: string;
  badgeColor: string;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserBadge {
  id: number;
  userId: string;
  businessHubId: number;
  badgeConfigId: number;
  currentProgress: number;
  isAchieved: boolean;
  achievedAt?: string;
  bonusAwarded: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface BadgeWithProgress {
  badge: UserBadge;
  config: BadgeConfig;
  isAchieved: boolean;
  businessHubName?: string;
  currentProgress: number;
  businessHubAvatar?: string;
  businessHubId?: number;
  progressPercentage: number;
}

export interface HubBadgesResponse {
  businessHub: {
    id: number;
    name: string;
    avatar?: string;
    city?: string;
  };
  badges: BadgeWithProgress[];
  total: number;
  achieved: number;
}

export interface MyBadgesResponse {
  badges: BadgeWithProgress[];
  total: number;
  totalAchieved: number;
}

@Injectable({
  providedIn: 'root'
})
export class BadgeService {
  private apiUrl = `${environment.apiUrl}/business/badges`;

  constructor(private http: HttpClient) { }

  // Get all badges for current user across all hubs
  getMyBadges(): Observable<any> {
    
    return this.http.get<any>(`${this.apiUrl}/my-badges`);
  }

  // Get all badges for a specific hub
  getHubBadges(hubId: number): Observable<HubBadgesResponse> {
    return this.http.get<HubBadgesResponse>(`${this.apiUrl}/hub/${hubId}`);
  }

  // Business owner endpoints
  getAllConfigs(): Observable<any> {
    return this.http.get(`${this.apiUrl}/configs`);
  }

  getConfig(configId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/configs/${configId}`);
  }

  createConfig(config: Partial<BadgeConfig>): Observable<any> {
    return this.http.post(`${this.apiUrl}/configs`, config);
  }

  updateConfig(configId: number, config: Partial<BadgeConfig>): Observable<any> {
    return this.http.put(`${this.apiUrl}/configs/${configId}`, config);
  }

  deleteConfig(configId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/configs/${configId}`);
  }

  // Stats
  getHubStats(): Observable<any> {
    return this.http.get(`${this.apiUrl}/stats`);
  }

  getConfigStats(configId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/configs/${configId}/stats`);
  }

  getTopEarners(limit: number = 10): Observable<any> {
    return this.http.get(`${this.apiUrl}/top-earners`, {
      params: { limit: limit.toString() }
    });
  }
}